import {GridIdNameModel, GridRowCellProps} from './types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styles from './grid.module.scss';
import EditCell from './components/edit-cell';
import Cell from './components/cell';
import classNames from 'classnames';
import {Tooltip} from '@mui/material';

const GridRowCell = (props: GridRowCellProps) => {
    const { column, row, field, rowEdit, editState, onCellEditStart, onCellEditStop, setBackground, disabled } = props;
    const editable = useMemo<boolean>(() => {
        const editableValue = column.editable;
        if (!editableValue) {
            return false;
        }
        if (typeof editableValue === 'function') {
            return editableValue(row);
        }

        return editableValue.valueOf();
    }, [column, row]);
    const [options, setOptions] = useState<GridIdNameModel[]>(() => {
        const selectableOptions = column.selectableOptions;
        if (!selectableOptions) {
            return [];
        }

        if (typeof selectableOptions === 'function') {
            return [];
        }

        return selectableOptions;
    });
    
    const [edit, setEdit] = useState<boolean>(false)
    const resolvedValue = useMemo<any>(() => column.value ? column.value(row) : row[field], [column, row, field]);
    const computeDisplayValue = useCallback((value?: any) => column.displayValue
        ? column.displayValue(value ?? resolvedValue, row, props.intl)
        : (value ?? row[field]), [column, props.intl, resolvedValue, row, field]);
    const [displayValue, setDisplayValue] = useState<any>(() => computeDisplayValue(resolvedValue));
    const placeholder = props.displayPlaceholder ?
        column.type === 'singleSelect'
            ? displayValue
            : column.localizationKey !== undefined ? props.intl.formatMessage({id: column.localizationKey})
                : undefined
        : undefined;

    const handleContainerLeave = () => {
        if (!edit) {
            return
        }

        setEdit(false)
    }

    const handleContainerClick = () => {
        if (!editable || disabled) {
            return
        }

        setEdit(true)
        onCellEditStart?.(field, resolvedValue);
    }

    const handleEditCellChange = (value: any) => {
        if (value === undefined) {
            return;
        }

        setDisplayValue(edit ?
            column.displayValue ?
                column.displayValue(value, row, props.intl)
                : value
            : column.displayValue ?
                column.displayValue(value, row, props.intl)
                : row[field])

        setEdit(false)


        onCellEditStop?.(field, value)
    }

    useEffect(() => {
        setDisplayValue(computeDisplayValue(resolvedValue));
    }, [computeDisplayValue, resolvedValue]);

    useEffect(() => {
        const selectableOptions = column.selectableOptions;
        if (!selectableOptions) {
            setOptions([]);
            return;
        }

        if (typeof selectableOptions === 'function') {
            if (!props.lazyDataLoad) {
                selectableOptions(row).then((r) => setOptions(r));
            } else {
                setOptions([]);
            }
            return;
        }

        setOptions(selectableOptions);
    }, [column.selectableOptions, props.lazyDataLoad, row]);

    useEffect(() => {
        if (editState === null) {
            setDisplayValue(computeDisplayValue(resolvedValue));
            setEdit(false);
        }
    }, [computeDisplayValue, editState, resolvedValue]);

    useEffect(() => {
        if (edit && !rowEdit) {
            setEdit(false);
        }
    }, [rowEdit]);

    const cellRenderStyle: React.CSSProperties = {
        gridColumn: `${props.gridColumn.s} / ${props.gridColumn.e}`,
        display: 'flex',
        //minheight: 'var(--grid-row-height, 56px)',
        minHeight: 'var(--grid-row-height, 56px)',
        alignItems: 'flex-end'
    };
    const cellStyle: React.CSSProperties = {
        gridColumn: `${props.gridColumn.s} / ${props.gridColumn.e}`
    };

    if (column.alignment === 'center') {
        cellRenderStyle.justifyContent = 'center';
        cellRenderStyle.alignItems = 'center';
        cellStyle.display = 'flex';
        cellStyle.alignItems = 'center';
        cellStyle.justifyContent = 'center';
    } else if (column.alignment === 'end') {
        cellRenderStyle.justifyContent = 'center';
        cellRenderStyle.alignItems = 'flex-end';
        cellStyle.display = 'flex';
        cellStyle.alignItems = 'flex-end';
        cellStyle.justifyContent = 'center';
    } else if (column.alignment === 'start') {
        cellRenderStyle.justifyContent = 'center';
        cellRenderStyle.alignItems = 'flex-start';
        cellStyle.display = 'flex';
        cellStyle.alignItems = 'flex-start';
        cellStyle.justifyContent = 'center';
    }

    const dateEditDisplayValue = column.type === 'date' && React.isValidElement(displayValue)
        ? displayValue
        : undefined;
    const cellPrefix = column.cellPrefix?.(row, props.intl);
    const hasCellPrefix = Boolean(cellPrefix);
    const cellTitle = column.cellTitle?.(row, props.intl);
    const renderCellContent = (content: React.ReactNode): React.ReactNode => cellPrefix ? (
        <span className={styles.cellContentWithPrefix}>
            <span className={styles.cellPrefix}>{cellPrefix}</span>
            <span className={styles.cellMainContent}>{content}</span>
        </span>
    ) : content;
    const renderWithTooltip = (content: React.ReactNode): React.ReactNode => cellTitle
        ? <Tooltip title={cellTitle}><span className={styles.cellTooltipTarget}>{content}</span></Tooltip>
        : content;

    return (<>
        {column.visible === undefined || column.visible === true ?
            column.cellRender ? <div className={classNames(styles.baseCell, column.cellClassName, {
                [styles.cellWithPrefix]: hasCellPrefix,
            })}
            style={cellRenderStyle}>{!edit ? renderWithTooltip(renderCellContent(column!.cellRender(field, row, props.intl, disabled)))
                    : renderWithTooltip(renderCellContent(<EditCell type={column.type ?? 'string'}
                        value={column.value ? column.value(row) : row[field]}
                        onChange={handleEditCellChange}
                        active={edit}
                        placeholder={placeholder}
                        options={props.lazyDataLoad ? [] : options }
                        lazyLoad={props.lazyDataLoad}
                        min={column.min}
                        max={column.max}
                        minDate={column.minDate}
                        maxDate={column.maxDate}
                        step={column.step}
                        displayValue={dateEditDisplayValue}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        getOptions={ props.lazyDataLoad && column.selectableOptions !== undefined && typeof column.selectableOptions === 'function' ? () => column.selectableOptions(row) : undefined } />))}
            </div>
                : <Cell onOutsideClick={handleContainerLeave}
                    cellProps={{
                        onClick: () => handleContainerClick(),
                        style: cellStyle
                    }}
                    className={classNames(column.cellClassName, {
                        [styles.cellWithPrefix]: hasCellPrefix,
                    })}
                    flex={column.flex ?? false} muted={column.muted ?? false} 
                    editable={editable}
                    setBackground={setBackground}
                    type={column.type ?? 'string'}
                    placeholder={placeholder}
                    active={edit}
                    position={column.position ?? 'start'}
                    alignment={column.alignment ?? 'start'}
                    disabled={disabled}>
                    {renderWithTooltip(renderCellContent(edit && !disabled ? <EditCell type={column.type ?? 'string'}
                        value={column.value ? column.value(row) : row[field]}
                        onChange={handleEditCellChange}
                        options={options}
                        active={edit}
                        placeholder={placeholder}
                        lazyLoad={props.lazyDataLoad}
                        min={column.min}
                        max={column.max}
                        minDate={column.minDate}
                        maxDate={column.maxDate}
                        step={column.step}
                        displayValue={dateEditDisplayValue}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        getOptions={ props.lazyDataLoad && column.selectableOptions !== undefined && typeof column.selectableOptions === 'function' ? () => column.selectableOptions(row) : undefined }/>
                        : displayValue
                    ))}
                </Cell>
            : <></>}
    </>)
}

export default GridRowCell;
